import React, { useEffect, useRef, useState } from 'react';
import { Header } from 'components/Header';
import { Breadcrumbs } from 'components/_shared/Breadcrumbs';
import { Icon } from 'components/_shared/Icon';
import { ROUTES } from '_constants';
import { API } from 'api';
import {
  SectionsGrid,
  SectionCard,
  SectionTitle,
  FileList,
  FileItem,
  FileItemInfo,
  FileName,
  FileSize,
  RemoveButton,
  AddFileLabel,
  ProgressBarContainer,
  ProgressBarFill,
  ProgressBarText,
  UploadInfo,
  ActionRow,
  UploadButton,
  CancelButton
} from 'pages/Upload/Upload.styles';
import {
  UploadPageContainer,
  Tabs,
  Tab,
  TabsLine,
  TabContent,
  MissionsWrapper,
  MissionListHeader,
  ListTitle,
  MissionsContainer,
  MissionItemBlock,
  MissionItem,
  MissionItemInfo,
  MissionDateInfo,
  HelpText,
  ToggleButton,
  ListProgressBarItem,
  ListProgressBarContainer,
  ListProgressBarFill,
  ListProgressBarText,
  UploadFormWrapper,
  CombinedSize,
  SuccessOverlay,
  SuccessCard,
  SuccessIconWrap,
  SuccessTitle,
  SuccessText,
  SuccessActions,
  SuccessButton,
  StatusMessage,
  EmptyState,
  SortButton,
  MissionFileList,
  MissionFileRow,
  MissionFileName,
  MissionFileSize,
  DownloadButton,
  StatusGroup,
  StatusChip,
  DeleteMissionButton,
  FileCheckbox,
  RemoveFilesButton,
  ConfirmOverlay,
  ConfirmCard,
  ConfirmTitle,
  ConfirmText,
  ConfirmActions,
  ConfirmButton
} from './UploadMissions.styles';

const TABS = { LIST: 'Upload list', NEW: 'New Upload' };

const breadcrumbsItems = [{ link: ROUTES.ROOT, text: 'Home' }, { text: 'Upload data' }];

const UPLOAD_TYPES = {
  DATA_VIDEO: 'data_video',
  LOG: 'log',
  CALIBRATION: 'calibration_video'
};

const SIZE_WARN_MB = 5000;
const toMB = bytes => (bytes / 1024 / 1024).toFixed(2);
const mkFileItem = file => ({ file, progress: 0, uploading: false });

const DATA_VIDEO_RE = /^[A-Z0-9]+_UNIT\d{2}\d{14}_\d{4}\.MP4$/i;

// Returns the name the backend will store for calibration files
const getCalibDisplayName = file => {
  const ext = file.name.split('.').pop();
  return `calibration.${ext}`;
};

const STATUS_LABELS = {
  pending: 'Pending',
  in_progress: 'Uploading',
  completed: 'Done',
  failed: 'Failed'
};

const formatTimeLeft = (startTime, bytesLoaded, totalBytes) => {
  if (!startTime || bytesLoaded <= 0 || totalBytes <= 0) return null;
  const elapsed = (Date.now() - startTime) / 1000;
  if (elapsed < 2) return null;
  const speed = bytesLoaded / elapsed;
  const remaining = totalBytes - bytesLoaded;
  if (remaining <= 0) return null;
  const secs = Math.round(remaining / speed);
  if (secs < 60) return `~${secs}s left`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s > 0 ? `~${m}m ${s}s left` : `~${m}m left`;
};

const formatDate = iso => {
  const d = new Date(iso);
  return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1)
    .toString()
    .padStart(2, '0')}.${d.getFullYear()} ${d.getHours()}:${d
    .getMinutes()
    .toString()
    .padStart(2, '0')}`;
};

const getTrajectoryInfo = ts => {
  if (!ts) return { status: null, label: 'Not started' };
  if (ts.success) return { status: 'traj_done', label: 'Ready' };
  if (ts.error || (ts.finished_at && !ts.success))
    return { status: 'traj_failed', label: 'Failed' };
  if (ts.calculated) return { status: 'processing', label: 'Publishing…' };
  return { status: 'processing', label: 'Processing…' };
};

// Returns true when a mission still needs live status updates.
// Keep polling while upload is running OR trajectory exists but hasn't finished yet.
const needsPolling = m => {
  if (m.status === 'in_progress') return true;
  const ts = m.trajectory_status;
  if (!ts) return false;
  return !ts.success && !ts.error && !ts.finished_at;
};

export const UploadMissions = () => {
  const [selectedTab, setSelectedTab] = useState(TABS.LIST);

  // ── Mission list ──────────────────────────────────────────────────
  const [missions, setMissions] = useState([]);
  const [missionsLoading, setMissionsLoading] = useState(false);
  const [missionsError, setMissionsError] = useState(null);
  const [sortNewest, setSortNewest] = useState(true);

  // ── Upload form ───────────────────────────────────────────────────
  const [dcimFiles, setDcimFiles] = useState([]);
  const [invalidDcimFiles, setInvalidDcimFiles] = useState([]);
  const [gpsFiles, setGpsFiles] = useState([]);
  const [calibFile, setCalibFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const xhrRefs = useRef({});
  const missionIdRef = useRef(null);
  const getMissionsRef = useRef(null);

  // ── Live upload progress (shown in the list while uploading) ──────
  const [liveUpload, setLiveUpload] = useState(null);

  // ── Delete / remove-files confirmation modal ──────────────────────
  // confirmModal = null | { type: 'delete_mission', missionId }
  //              | { type: 'remove_files', missionId, fileNames }
  const [confirmModal, setConfirmModal] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmError, setConfirmError] = useState(null);

  // ── Per-mission file selection for completed missions ─────────────
  // Index-based: { [missionId]: Set<index> }
  // Using indices so selecting one file never accidentally selects
  // another file that happens to share the same name.
  const [fileSelections, setFileSelections] = useState({});

  const totalBytes =
    dcimFiles.reduce((s, f) => s + f.file.size, 0) +
    gpsFiles.reduce((s, f) => s + f.file.size, 0) +
    (calibFile ? calibFile.file.size : 0);

  const canStart =
    !uploading &&
    !!calibFile &&
    dcimFiles.length > 0 &&
    gpsFiles.length > 0 &&
    invalidDcimFiles.length === 0;

  // ── API ───────────────────────────────────────────────────────────

  const getMissions = () => {
    setMissionsLoading(true);
    setMissionsError(null);
    API.upload
      .getMissions()
      .then(({ data }) => {
        // Any in_progress mission that doesn't belong to the current session is
        // orphaned (upload was killed by a page reload / navigation). Mark it failed.
        // Also mark completed missions as failed when their trajectory request failed.
        const currentId = missionIdRef.current;
        data.forEach(m => {
          if (m.status === 'in_progress' && m.id !== currentId) {
            API.upload
              .updateMission(m.id, {
                status: 'failed',
                error: 'Upload interrupted (page was reloaded)'
              })
              .catch(() => {});
          }
        });

        setMissions(prev => {
          const detailsMap = Object.fromEntries(prev.map(m => [m.id, m.showDetails]));
          return data.map(m => ({
            ...m,
            status:
              m.status === 'in_progress' && m.id !== currentId ? 'failed' : m.status,
            showDetails: detailsMap[m.id] ?? false
          }));
        });
      })
      .catch(() => setMissionsError('Failed to load missions.'))
      .finally(() => setMissionsLoading(false));
  };
  getMissionsRef.current = getMissions;

  useEffect(() => {
    getMissions();
  }, []);

  // ── Auto-poll while any mission needs live updates ────────────────
  const needsLiveUpdates = missions.some(needsPolling);
  useEffect(() => {
    if (!needsLiveUpdates) return;
    const id = setInterval(() => getMissionsRef.current(), 10000);
    return () => clearInterval(id);
  }, [needsLiveUpdates]);

  // ── Mark upload as failed when navigating away mid-upload ─────────
  useEffect(() => {
    const handleBeforeUnload = e => {
      if (missionIdRef.current) {
        e.preventDefault();
        e.returnValue =
          'Upload in progress. If you leave, the upload will be marked as failed.';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // On component unmount (in-app navigation) mark upload as failed
  useEffect(() => {
    return () => {
      if (missionIdRef.current) {
        Object.values(xhrRefs.current).forEach(xhr => xhr && xhr.abort());
        API.upload
          .updateMission(missionIdRef.current, { status: 'failed' })
          .catch(() => {});
        missionIdRef.current = null;
      }
    };
  }, []);

  // ── Mission list helpers ──────────────────────────────────────────

  const toggleDetails = id => {
    setMissions(prev =>
      prev.map(m => (m.id === id ? { ...m, showDetails: !m.showDetails } : m))
    );
  };

  // ── Delete failed mission ─────────────────────────────────────────

  const handleDeleteMission = missionId => {
    setConfirmError(null);
    setConfirmModal({ type: 'delete_mission', missionId });
  };

  const confirmDeleteMission = async () => {
    const { missionId } = confirmModal;
    setConfirmLoading(true);
    setConfirmError(null);
    try {
      await API.upload.deleteMission(missionId);
      setMissions(prev => prev.filter(m => m.id !== missionId));
      setConfirmModal(null);
    } catch {
      setConfirmError('Failed to delete the upload. Please try again.');
    } finally {
      setConfirmLoading(false);
    }
  };

  // ── Remove files from completed mission ───────────────────────────

  // Toggle file selection by index so that two files with the same name
  // can be independently selected/deselected.
  const toggleFileSelection = (missionId, index) => {
    setFileSelections(prev => {
      const current = new Set(prev[missionId] || []);
      if (current.has(index)) {
        current.delete(index);
      } else {
        current.add(index);
      }
      return { ...prev, [missionId]: current };
    });
  };

  const handleRemoveFiles = missionId => {
    const selectedIndices = fileSelections[missionId];
    if (!selectedIndices || selectedIndices.size === 0) return;

    const mission = missions.find(m => m.id === missionId);
    if (!mission) return;

    // Convert selected indices to file names
    const fileNames = [...selectedIndices]
      .map(i => mission.uploaded_files[i]?.name)
      .filter(Boolean);

    setConfirmError(null);
    setConfirmModal({ type: 'remove_files', missionId, fileNames });
  };

  const confirmRemoveFiles = async () => {
    const { missionId, fileNames } = confirmModal;
    setConfirmLoading(true);
    setConfirmError(null);
    try {
      const { data } = await API.upload.removeMissionFiles(missionId, fileNames);
      setMissions(prev =>
        prev.map(m =>
          m.id === missionId ? { ...m, ...data, showDetails: m.showDetails } : m
        )
      );
      setFileSelections(prev => ({ ...prev, [missionId]: new Set() }));
      setConfirmModal(null);
    } catch {
      setConfirmError('Failed to remove files. Please try again.');
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleConfirm = () => {
    if (confirmModal?.type === 'delete_mission') confirmDeleteMission();
    else if (confirmModal?.type === 'remove_files') confirmRemoveFiles();
  };

  // ── Upload helpers ────────────────────────────────────────────────

  // onProgress(percent, loadedBytes)
  const uploadFileXHR = (uploadUrl, file, onProgress) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhrRefs.current[file.name] = xhr;
      xhr.open('PUT', uploadUrl, true);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.upload.onprogress = e => {
        if (e.lengthComputable)
          onProgress(Math.round((e.loaded / e.total) * 100), e.loaded);
      };
      xhr.onload = () =>
        xhr.status >= 200 && xhr.status < 300
          ? resolve()
          : reject(new Error(`Upload failed: HTTP ${xhr.status}`));
      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.send(file);
    });

  const handleUpload = async () => {
    setUploading(true);
    setUploadError(null);

    let missionId = null;
    let sessionFolder = null;

    try {
      const { data: mission } = await API.upload.createMission();
      missionId = mission.id;
      missionIdRef.current = missionId;
      sessionFolder = mission.gcs_path;
      setLiveUpload({
        missionId,
        createdAt: new Date().toISOString(),
        progress: 0,
        startTime: Date.now(),
        bytesLoaded: 0
      });
      setSelectedTab(TABS.LIST);
    } catch {
      setUploadError('Failed to start upload. Please try again.');
      setUploading(false);
      return;
    }

    const queue = [
      ...dcimFiles.map((f, i) => ({
        fileItem: f,
        type: UPLOAD_TYPES.DATA_VIDEO,
        markProgress: (p, u) =>
          setDcimFiles(prev =>
            prev.map((item, j) =>
              j === i ? { ...item, progress: p, uploading: u } : item
            )
          )
      })),
      ...gpsFiles.map((f, i) => ({
        fileItem: f,
        type: UPLOAD_TYPES.LOG,
        markProgress: (p, u) =>
          setGpsFiles(prev =>
            prev.map((item, j) =>
              j === i ? { ...item, progress: p, uploading: u } : item
            )
          )
      })),
      ...(calibFile
        ? [
            {
              fileItem: calibFile,
              type: UPLOAD_TYPES.CALIBRATION,
              markProgress: (p, u) =>
                setCalibFile(prev =>
                  prev ? { ...prev, progress: p, uploading: u } : null
                )
            }
          ]
        : [])
    ];

    const totalBytesAll = queue.reduce((s, item) => s + item.fileItem.file.size, 0);
    let totalBytesLoaded = 0;

    try {
      for (const item of queue) {
        const bytesBeforeThisFile = totalBytesLoaded;
        item.markProgress(0, true);
        const { data } = await API.upload.getUploadURL(
          item.fileItem.file.name,
          item.fileItem.file.type || 'application/octet-stream',
          item.type,
          sessionFolder
        );
        await uploadFileXHR(data.upload_url, item.fileItem.file, (percent, loaded) => {
          item.markProgress(percent, true);
          if (totalBytesAll > 0) {
            const overall = Math.round(
              ((bytesBeforeThisFile + loaded) / totalBytesAll) * 100
            );
            setLiveUpload(prev =>
              prev
                ? {
                    ...prev,
                    progress: overall,
                    bytesLoaded: bytesBeforeThisFile + loaded
                  }
                : null
            );
          }
        });
        totalBytesLoaded += item.fileItem.file.size;
        item.markProgress(100, false);
      }

      const uploadedFiles = [
        ...dcimFiles.map(f => ({
          name: f.file.name,
          type: UPLOAD_TYPES.DATA_VIDEO,
          size: f.file.size
        })),
        ...gpsFiles.map(f => ({
          name: f.file.name,
          type: UPLOAD_TYPES.LOG,
          size: f.file.size
        })),
        // Use the server-side renamed calibration filename so the stored name
        // matches the actual GCS object name.
        ...(calibFile
          ? [
              {
                name: getCalibDisplayName(calibFile.file),
                type: UPLOAD_TYPES.CALIBRATION,
                size: calibFile.file.size
              }
            ]
          : [])
      ];
      await API.upload.updateMission(missionId, {
        status: 'completed',
        uploaded_files: uploadedFiles
      });
      missionIdRef.current = null;
      setDcimFiles([]);
      setInvalidDcimFiles([]);
      setGpsFiles([]);
      setCalibFile(null);
      setLiveUpload(null);
      getMissions();
      setShowSuccess(true);
    } catch (err) {
      const errorMsg =
        (typeof err === 'string' ? err : null) ||
        err?.data?.detail ||
        err?.response?.data?.detail ||
        err?.message ||
        (err?.status ? `Server error (HTTP ${err.status})` : null) ||
        'Upload failed. Please try again.';
      if (missionIdRef.current) {
        API.upload
          .updateMission(missionIdRef.current, { status: 'failed', error: errorMsg })
          .catch(() => {});
        missionIdRef.current = null;
      }
      setLiveUpload(null);
      setUploadError(errorMsg);
    } finally {
      setUploading(false);
      xhrRefs.current = {};
    }
  };

  // Cancel upload: abort all in-flight XHRs, mark the mission as failed,
  // then delete it (which removes all partially-uploaded GCS files and the
  // session folder), and finally refresh the mission list.
  const cancelUpload = () => {
    Object.values(xhrRefs.current).forEach(xhr => xhr && xhr.abort());
    xhrRefs.current = {};
    setUploading(false);
    setLiveUpload(null);
    setDcimFiles(prev => prev.map(f => ({ ...f, progress: 0, uploading: false })));
    setGpsFiles(prev => prev.map(f => ({ ...f, progress: 0, uploading: false })));
    setCalibFile(prev => (prev ? { ...prev, progress: 0, uploading: false } : null));

    const id = missionIdRef.current;
    missionIdRef.current = null;
    if (id) {
      API.upload
        .updateMission(id, { status: 'failed', error: 'Upload cancelled' })
        .then(() => API.upload.deleteMission(id))
        .catch(() => {})
        .finally(() => getMissionsRef.current());
    }
  };

  const handleDcimChange = e => {
    const files = Array.from(e.target.files);
    const valid = files.filter(f => DATA_VIDEO_RE.test(f.name));
    const invalid = files.filter(f => !DATA_VIDEO_RE.test(f.name));
    if (valid.length > 0) {
      setDcimFiles(prev => {
        const names = new Set(prev.map(f => f.file.name));
        return [...prev, ...valid.filter(f => !names.has(f.name)).map(mkFileItem)];
      });
    }
    if (invalid.length > 0) {
      setInvalidDcimFiles(prev => {
        const names = new Set(prev.map(f => f.name));
        return [...prev, ...invalid.filter(f => !names.has(f.name))];
      });
    }
    e.target.value = '';
  };

  const handleGpsChange = e => {
    const files = Array.from(e.target.files).filter(f => f.name.endsWith('.log'));
    setGpsFiles(prev => {
      const names = new Set(prev.map(f => f.file.name));
      return [...prev, ...files.filter(f => !names.has(f.name)).map(mkFileItem)];
    });
    e.target.value = '';
  };

  const handleCalibChange = e => {
    const file = e.target.files[0];
    if (file) setCalibFile(mkFileItem(file));
    e.target.value = '';
  };

  const handleRerunTrajectory = async missionId => {
    try {
      const { data } = await API.upload.rerunTrajectory(missionId);
      setMissions(prev =>
        prev.map(m =>
          m.id === missionId ? { ...m, ...data, showDetails: m.showDetails } : m
        )
      );
    } catch {
      // silently ignore — polling will reflect actual state
    }
  };

  const handleDownload = async (fileName, fileType, gcsPath) => {
    try {
      const { data } = await API.upload.getDownloadURL(fileName, fileType, gcsPath);
      window.open(data.download_url, '_blank');
    } catch {
      // silently ignore
    }
  };

  // displayName overrides item.file.name in the row (used for calibration
  // to show the server-renamed filename instead of the original).
  const renderFileRow = (item, onRemove, displayName) => {
    const nameToShow = displayName || item.file.name;
    const sizeMB = parseFloat(toMB(item.file.size));
    return (
      <FileItem key={nameToShow}>
        <FileName title={nameToShow}>{nameToShow}</FileName>
        <FileItemInfo>
          <FileSize style={sizeMB > SIZE_WARN_MB ? { color: 'red' } : undefined}>
            {sizeMB} MB{sizeMB > SIZE_WARN_MB ? ' (!)' : ''}
          </FileSize>
          {!uploading && (
            <RemoveButton onClick={onRemove}>
              <Icon>Delete</Icon>
            </RemoveButton>
          )}
          {(item.uploading || item.progress === 100) && (
            <ProgressBarContainer>
              <ProgressBarFill
                $width={item.progress}
                $done={!item.uploading && item.progress === 100}
              />
              <ProgressBarText>
                {item.progress === 100 ? 'Done' : `${item.progress}%`}
              </ProgressBarText>
            </ProgressBarContainer>
          )}
        </FileItemInfo>
      </FileItem>
    );
  };

  // ── Tab renders ───────────────────────────────────────────────────

  const renderMissionList = () => (
    <MissionsWrapper>
      <MissionListHeader>
        <ListTitle>Upload list</ListTitle>
        <SortButton onClick={() => setSortNewest(prev => !prev)}>
          <Icon width={14} height={14}>
            {sortNewest ? 'ExpandDown' : 'ExpandUp'}
          </Icon>
          {sortNewest ? 'Newest first' : 'Oldest first'}
        </SortButton>
      </MissionListHeader>

      {uploadError && (
        <StatusMessage style={{ marginBottom: 12 }}>
          {uploadError}{' '}
          <button
            onClick={() => {
              setUploadError(null);
              setSelectedTab(TABS.NEW);
            }}
            style={{
              marginLeft: 8,
              cursor: 'pointer',
              border: 'none',
              background: 'none',
              textDecoration: 'underline',
              color: 'inherit',
              fontSize: 'inherit'
            }}
          >
            Try again
          </button>
        </StatusMessage>
      )}

      <MissionsContainer>
        {/* Live upload entry while uploading in background */}
        {uploading && liveUpload && (
          <MissionItemBlock key='live-upload'>
            <MissionItem>
              <HelpText fontSize={15}>{formatDate(liveUpload.createdAt)}</HelpText>
              <MissionItemInfo>
                <ListProgressBarItem>
                  {(() => {
                    const timeLeft = formatTimeLeft(
                      liveUpload.startTime,
                      liveUpload.bytesLoaded,
                      totalBytes
                    );
                    return (
                      <ListProgressBarContainer title='Uploading…'>
                        <ListProgressBarFill $width={liveUpload.progress} />
                        <ListProgressBarText>
                          {liveUpload.progress}%{timeLeft ? ` · ${timeLeft}` : ''}
                        </ListProgressBarText>
                      </ListProgressBarContainer>
                    );
                  })()}
                </ListProgressBarItem>
                <CancelButton
                  onClick={cancelUpload}
                  style={{ padding: '2px 10px', fontSize: 12 }}
                >
                  Cancel
                </CancelButton>
              </MissionItemInfo>
            </MissionItem>
          </MissionItemBlock>
        )}

        {missionsLoading && <EmptyState>Loading…</EmptyState>}
        {missionsError && <EmptyState>{missionsError}</EmptyState>}
        {!missionsLoading &&
          !missionsError &&
          missions.length === 0 &&
          !(uploading && liveUpload) && (
            <EmptyState>
              No uploads yet. Switch to &ldquo;New Upload&rdquo; to get started.
            </EmptyState>
          )}

        {missions
          .filter(m => !(uploading && m.id === missionIdRef.current))
          .slice()
          .sort((a, b) =>
            sortNewest
              ? new Date(b.created_at) - new Date(a.created_at)
              : new Date(a.created_at) - new Date(b.created_at)
          )
          .map(mission => {
            const trajInfo = getTrajectoryInfo(mission.trajectory_status);
            const hasFiles = mission.uploaded_files && mission.uploaded_files.length > 0;
            const isFailed = mission.status === 'failed';
            const isCompleted = mission.status === 'completed';
            const selectedIndices = fileSelections[mission.id] || new Set();
            return (
              <MissionItemBlock key={mission.id}>
                {/* ── Collapsed row ── */}
                <MissionItem>
                  <HelpText fontSize={15}>{formatDate(mission.created_at)}</HelpText>
                  <MissionItemInfo>
                    <StatusGroup>
                      <StatusChip
                        $status={mission.status}
                        title={`Upload: ${
                          STATUS_LABELS[mission.status] || mission.status
                        }`}
                      >
                        <Icon width={11} height={11}>
                          Upload
                        </Icon>
                        {STATUS_LABELS[mission.status] || mission.status}
                      </StatusChip>
                      {mission.trajectory_status && (
                        <StatusChip
                          $status={trajInfo.status}
                          title={`Preview: ${trajInfo.label}`}
                        >
                          <Icon width={11} height={11}>
                            Image
                          </Icon>
                          {trajInfo.label}
                        </StatusChip>
                      )}
                    </StatusGroup>
                    {trajInfo.status === 'traj_failed' && (
                      <RemoveFilesButton
                        title='Re-run trajectory processing'
                        onClick={() => handleRerunTrajectory(mission.id)}
                        style={{ padding: '2px 10px', fontSize: 12 }}
                      >
                        Re-run
                      </RemoveFilesButton>
                    )}
                    {isFailed && (
                      <DeleteMissionButton
                        title='Delete this upload and remove its files from storage'
                        onClick={() => handleDeleteMission(mission.id)}
                      >
                        <Icon width={16} height={16}>
                          Delete
                        </Icon>
                      </DeleteMissionButton>
                    )}
                    <ToggleButton
                      title='Show / hide session details'
                      onClick={() => toggleDetails(mission.id)}
                    >
                      <Icon width={20} height={20}>
                        {mission.showDetails ? 'ExpandUp' : 'ExpandDown'}
                      </Icon>
                    </ToggleButton>
                  </MissionItemInfo>
                </MissionItem>

                {/* ── Error message (upload or processing failure) ── */}
                {(isFailed || trajInfo.status === 'traj_failed') &&
                  (mission.error || mission.trajectory_status?.error) && (
                    <StatusMessage style={{ padding: '2px 12px 6px', fontSize: 12 }}>
                      {mission.error || mission.trajectory_status?.error}
                    </StatusMessage>
                  )}

                {/* ── Expanded: session metadata + file list ── */}
                {mission.showDetails && (
                  <>
                    <MissionDateInfo>
                      <HelpText fontSize={13}>
                        Session:&nbsp;<strong>{mission.gcs_path}</strong>
                      </HelpText>
                      <HelpText fontSize={13}>
                        Uploaded:&nbsp;{formatDate(mission.created_at)}
                      </HelpText>
                    </MissionDateInfo>
                    {hasFiles && (
                      <>
                        <MissionFileList>
                          {mission.uploaded_files.map((f, idx) => (
                            <MissionFileRow key={idx}>
                              {/* Only data_video files can be selected for removal */}
                              {isCompleted && f.type === UPLOAD_TYPES.DATA_VIDEO && (
                                <FileCheckbox
                                  checked={selectedIndices.has(idx)}
                                  onChange={() => toggleFileSelection(mission.id, idx)}
                                  title='Select to remove'
                                />
                              )}
                              <MissionFileName title={f.name}>{f.name}</MissionFileName>
                              {f.size != null && (
                                <MissionFileSize>{toMB(f.size)} MB</MissionFileSize>
                              )}
                              <DownloadButton
                                title='Download file'
                                onClick={() =>
                                  handleDownload(f.name, f.type, mission.gcs_path)
                                }
                              >
                                <Icon width={16} height={16}>
                                  Download
                                </Icon>
                              </DownloadButton>
                            </MissionFileRow>
                          ))}
                        </MissionFileList>
                        {isCompleted && selectedIndices.size > 0 && (
                          <RemoveFilesButton
                            onClick={() => handleRemoveFiles(mission.id)}
                          >
                            <Icon width={14} height={14}>
                              Delete
                            </Icon>
                            Remove {selectedIndices.size} video
                            {selectedIndices.size > 1 ? 's' : ''} &amp; re-run trajectory
                          </RemoveFilesButton>
                        )}
                      </>
                    )}
                  </>
                )}
              </MissionItemBlock>
            );
          })}
      </MissionsContainer>
    </MissionsWrapper>
  );

  const renderNewUpload = () => (
    <UploadFormWrapper>
      {/* 3-column grid: Data Videos | Calibration | GPS Logs */}
      <SectionsGrid style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        {/* Data Videos */}
        <SectionCard>
          <SectionTitle>Videos</SectionTitle>
          {dcimFiles.length === 0 && invalidDcimFiles.length === 0 ? (
            <UploadInfo>No videos added yet</UploadInfo>
          ) : (
            <FileList>
              {dcimFiles.map((item, i) =>
                renderFileRow(item, () =>
                  setDcimFiles(prev => prev.filter((_, j) => j !== i))
                )
              )}
              {invalidDcimFiles.map(file => (
                <FileItem key={file.name}>
                  <FileName title={file.name} style={{ color: 'red' }}>
                    {file.name}
                  </FileName>
                  <FileItemInfo>
                    <FileSize style={{ color: 'red' }}>Invalid name</FileSize>
                    {!uploading && (
                      <RemoveButton
                        onClick={() =>
                          setInvalidDcimFiles(prev =>
                            prev.filter(f => f.name !== file.name)
                          )
                        }
                      >
                        <Icon>Delete</Icon>
                      </RemoveButton>
                    )}
                  </FileItemInfo>
                </FileItem>
              ))}
            </FileList>
          )}
          {invalidDcimFiles.length > 0 && (
            <StatusMessage style={{ fontSize: 11, marginTop: 4 }}>
              Expected: PREFIX_UNITnn&#123;YYYYMMDDHHMMSS&#125;_&#123;ssss&#125;.MP4
            </StatusMessage>
          )}
          <AddFileLabel htmlFor='dcim-input' $disabled={uploading}>
            <Icon>Plus</Icon> Add Videos
          </AddFileLabel>
          <input
            id='dcim-input'
            type='file'
            multiple
            accept='.mp4,video/mp4'
            onChange={handleDcimChange}
            disabled={uploading}
            style={{ display: 'none' }}
          />
        </SectionCard>

        {/* Calibration Video */}
        <SectionCard>
          <SectionTitle>Calibration</SectionTitle>
          {!calibFile ? (
            <UploadInfo>No calibration video added yet</UploadInfo>
          ) : (
            <FileList>
              {renderFileRow(
                calibFile,
                () => setCalibFile(null),
                getCalibDisplayName(calibFile.file)
              )}
            </FileList>
          )}
          <AddFileLabel htmlFor='calib-input' $disabled={uploading}>
            <Icon>Plus</Icon> {calibFile ? 'Replace Video' : 'Add Video'}
          </AddFileLabel>
          <input
            id='calib-input'
            type='file'
            accept='.mp4,video/mp4'
            onChange={handleCalibChange}
            disabled={uploading}
            style={{ display: 'none' }}
          />
        </SectionCard>

        {/* GPS Logs */}
        <SectionCard>
          <SectionTitle>GPS Logs</SectionTitle>
          {gpsFiles.length === 0 ? (
            <UploadInfo>No log files added yet</UploadInfo>
          ) : (
            <FileList>
              {gpsFiles.map((item, i) =>
                renderFileRow(item, () =>
                  setGpsFiles(prev => prev.filter((_, j) => j !== i))
                )
              )}
            </FileList>
          )}
          <AddFileLabel htmlFor='gps-input' $disabled={uploading}>
            <Icon>Plus</Icon> Add Log Files
          </AddFileLabel>
          <input
            id='gps-input'
            type='file'
            multiple
            accept='.log'
            onChange={handleGpsChange}
            disabled={uploading}
            style={{ display: 'none' }}
          />
        </SectionCard>
      </SectionsGrid>

      {totalBytes > 0 && <CombinedSize>Total size: {toMB(totalBytes)} MB</CombinedSize>}

      <ActionRow>
        {uploadError && <StatusMessage>{uploadError}</StatusMessage>}
        {uploading ? (
          <CancelButton onClick={cancelUpload}>Cancel</CancelButton>
        ) : (
          <UploadButton
            disabled={!canStart}
            onClick={handleUpload}
            icon={<Icon>Upload</Icon>}
          >
            Start upload
          </UploadButton>
        )}
      </ActionRow>
    </UploadFormWrapper>
  );

  // ── Render ────────────────────────────────────────────────────────

  return (
    <>
      <Header />
      <UploadPageContainer>
        <Breadcrumbs items={breadcrumbsItems} />

        <Tabs>
          {Object.values(TABS).map(tab => (
            <Tab
              key={tab}
              $selected={selectedTab === tab}
              onClick={() => setSelectedTab(tab)}
            >
              {tab}
            </Tab>
          ))}
          <TabsLine />
        </Tabs>

        <TabContent>
          {selectedTab === TABS.LIST ? renderMissionList() : renderNewUpload()}
        </TabContent>
      </UploadPageContainer>

      {confirmModal && (
        <ConfirmOverlay>
          <ConfirmCard>
            {confirmModal.type === 'delete_mission' ? (
              <>
                <ConfirmTitle>Delete upload?</ConfirmTitle>
                <ConfirmText>
                  This will remove the session record and delete all uploaded files from
                  storage. This cannot be undone.
                </ConfirmText>
              </>
            ) : (
              <>
                <ConfirmTitle>Remove files &amp; re-run?</ConfirmTitle>
                <ConfirmText>
                  {confirmModal.fileNames.length} video
                  {confirmModal.fileNames.length > 1 ? 's' : ''} will be deleted from
                  storage and trajectory processing will be restarted. This cannot be
                  undone.
                </ConfirmText>
              </>
            )}
            {confirmError && (
              <StatusMessage style={{ marginBottom: 12 }}>{confirmError}</StatusMessage>
            )}
            <ConfirmActions>
              <ConfirmButton $danger onClick={handleConfirm} disabled={confirmLoading}>
                {confirmLoading ? 'Processing…' : 'Confirm'}
              </ConfirmButton>
              <ConfirmButton
                onClick={() => {
                  setConfirmModal(null);
                  setConfirmError(null);
                }}
                disabled={confirmLoading}
              >
                Cancel
              </ConfirmButton>
            </ConfirmActions>
          </ConfirmCard>
        </ConfirmOverlay>
      )}

      {showSuccess && (
        <SuccessOverlay>
          <SuccessCard>
            <SuccessIconWrap>✓</SuccessIconWrap>
            <SuccessTitle>Upload complete!</SuccessTitle>
            <SuccessText>All files have been uploaded successfully.</SuccessText>
            <SuccessActions>
              <SuccessButton
                onClick={() => {
                  setShowSuccess(false);
                  setSelectedTab(TABS.LIST);
                }}
              >
                View uploads
              </SuccessButton>
              <SuccessButton
                $secondary
                onClick={() => {
                  setShowSuccess(false);
                  setSelectedTab(TABS.NEW);
                }}
              >
                Upload more
              </SuccessButton>
            </SuccessActions>
          </SuccessCard>
        </SuccessOverlay>
      )}
    </>
  );
};
